// «What's new» для пользователей: окно «?» → вкладка What's new. Новые записи сверху,
// дата — день выкатки, version — сквозной номер (v.1, v.2…), name — имя выкатки (тема — покерный стол, без скрытого смысла: несколько
// выкаток в день и повод улыбнуться). Пишется ритуалом /changelog из коммитов, видимых пользователю.
export type ChangelogEntry = { date: string; version: number; name: string; added?: string[]; changed?: string[]; fixed?: string[] };

export const CHANGELOG: ChangelogEntry[] = [
  {
    date: '2026-09-17',
    version: 7,
    name: 'River',
    added: [
      'Assignees and team members can be reordered: drag a chip or press Alt+←/→. The order is the order of the colour stripes on the task bar, and the order a team fills in.',
      'Quick task has a Create button, and Enter creates the task from anywhere in the form, including after picking people with the mouse.',
      'Notifications live in one place: the bottom-left corner. Connection issues show there after a few seconds, and «Connection restored» confirms when it is back.',
    ],
    changed: [
      'A project with subprojects is now a coloured header row. Tasks are created in its subprojects; the task card picks the first subproject by default and no longer offers «no subproject».',
      '«+ Task» from the toolbar and a row\'s «+» start the task on the current week, not at the start of the visible period.',
      'People suggestions rank by the start of the name: «i» offers Ilya and Irina before Dima.',
      'The connection notice no longer pushes the page down.',
    ],
    fixed: [
      'A tab that had survived a network hiccup could keep reconnecting every two seconds, flashing the notice and hammering the server. One reconnect now.',
      'Reordering assignees in a task card is saved, and the History tab of a task shows assignee changes: added, removed, reordered.',
      'Saving your own assignee changes no longer shows «changed while you were editing» with someone else\'s name.',
      'Login shows the actual error after a failed Google sign-in and clears it from the address bar. Login card text is centred.',
    ],
  },
  {
    date: '2026-09-15',
    version: 6,
    name: 'Showdown',
    added: [
      'History: every change in one journal, grouped by action. Filter by author, period, object type and field, search, «Mine» and «Deleted» chips; Load more; new actions arrive live.',
      'Revert a change or a whole action, restore a deleted object with everything it took along; a warning when the same field was changed later; a «reverted» badge on the original.',
      'History tab in every card: task, launch task, project, event, vacation, launch. From a task row in History: open the card or «Show on the grid».',
      'Roles: none, viewer, member, admin. A viewer sees everything, asks and answers questions, but cannot move or edit; the plan shows no editing controls to them.',
      'Admin: one control panel. People and their accounts side by side, link from either side; «On the plan» (assignable, questions only, left); teams as chips; the role new accounts start with; default project order.',
      'Deleting a duplicate person offers to merge them into the real one: assignments, vacations, teams and questions move over.',
      'Add a person straight from the question form when the author is not in the list; they can ask, but are not assignable until an admin says so.',
      'Display is personal: row order, pins and highlight are yours only. Admins set the default project order; the View menu says when yours differs and offers a reset.',
      'Quick task: right-click empty grid → «Quick task here». Title and assignees under the outline, Enter creates a one-week task, Ctrl+Enter opens the full card. On the Launch Board — a three-day task.',
      'Task card in two columns: assignees as chips with suggestions and teams, the period with −/+ week and dates inline, project picker with colour and search, Confirmed and At risk as two checkboxes.',
      'Right-click menus everywhere: rows and their empty space (new event or vacation on the week under the cursor), vacations, questions, backlog cards. Row buttons moved into the menus; only «+» stays.',
      "Tooltips are white panels like menus, with the task's dates, people and marks; on Timeline chips they name the people too.",
      "Who's here: a pill with avatars; your own avatar with the connection dot in the account menu. Others' attention shows on release cards in Weeks too.",
      'Ctrl+Enter saves any card. Closing a task or event card with unsaved edits asks first.',
    ],
    changed: [
      'Grid: month lines stronger than weeks, quarters read as months; a project header without tasks sticks to the top while its subprojects scroll; a ghost row shows where a dragged row lands.',
      'Long bar titles fade out instead of ending in «…»; on a narrow bar only the marks stay. Vacations are a hatched area, not a grey slab.',
      'Dates in one style: «7–14 Oct 2026». Menu items have icons. Events no longer have a kind; holidays are a fixed calendar shaded as weekends.',
      'Agenda shows avatars and clamps long questions; History shows assignee changes as one field. Design panel counts only tasks in the visible period.',
      'The Help window covers the Launch Board; the empty Settings entry is gone.',
      'Roles change without a reload; a demoted admin loses the Admin tab at once.',
    ],
    fixed: [
      '«Reconnecting…» no longer stays forever after a lost connection: the channel reconnects by itself.',
      "A lost «I let go» no longer leaves someone's attention frame on a task for good; it fades within 20 seconds.",
      "Ctrl+P on the Launch Board no longer blocks the browser's print dialog.",
      'The «Blocked by» picker no longer offers tasks that would create a cycle.',
      'Reverting a lone assignment change in History now works; long question texts are clipped in row titles; resolve times are readable.',
      'Viewers were labelled «Member» in the account menu; History showed raw role codes.',
      'Right-clicking where no actions exist no longer opens an empty menu.',
      'Event and vacation cards ask before deleting, like the other cards.',
      'If the roadmap cannot load, a message with «Reload» replaces the endless «Loading…». A setting that failed to save reverts and says why.',
      'Saving also pauses after repeated failed inserts, not only failed edits. Row reorder no longer stutters while auto-scrolling.',
    ],
  },
  {
    date: '2026-09-11',
    version: 5,
    name: 'All In',
    added: [
      'Launch Board: a new tab for launches — a day-by-day board with swimlanes and tracks, bars coloured by status (queued, in progress, done, at risk, release). The three old launches are here with their tasks.',
      "Pick a launch in the toolbar; create one, edit its name and dates or delete it. The board spans the launch's dates and opens at its start.",
      'Launch task card: title, row, dates, status, notes and «Blocked by». A task shows as blocked until every blocker is done; arrows on the board show who blocks whom and light up when you hover a task.',
      'Move bars by days, stretch them by the edges; ← → by a day, ↑ ↓ to the next track, Shift+← → longer or shorter. Select several with a frame or Ctrl+click and move them together.',
      'Rows: rename in place (double-click or ✎), add tracks and swimlanes, reorder by the handle, pick a swimlane colour by its dot. Right-click empty space → New task here.',
      'View → Full titles on the Launch Board; a status legend in the toolbar.',
    ],
    changed: [
      "The header is one line: logo, tabs (Launch Board at the right), who's here, «?» and your avatar. History and Settings moved into the avatar menu with your name, role, connection and last sync.",
      'Toolbars share one order on every screen: actions on the left, then what and when you look at, View, filters, and counters at the right.',
      'Every screen remembers where you scrolled while the app is open.',
      'After repeated failures to save the same item, saving pauses and a red banner asks you to reload the page.',
    ],
    fixed: [
      "Saving over someone else's fresh edit is reported as a conflict, not as a server error.",
    ],
  },
  {
    date: '2026-09-10',
    version: 4,
    name: 'Full House',
    added: [
      'Release Plan is back, in two views. Weeks: releases by month and week, a card per release with ✓ confirmed, ! at risk, ★ key release and × not deploying.',
      'Weeks: drag a card to another week; search, project chips, 3 or 6 months from this week.',
      'Timeline: a lane per block with the month band and counts, events as context, release chips above and below the axis, draggable between weeks; admins edit the lanes.',
      'Task card: Key release and Confidence. Bars show ★ ! ✓ before the title on Projects and People (toggle «Release marks» in View).',
      "Project card: the project's colour (a dot before its name on the grid, the stripe on release cards) and its Timeline lanes. A new project lands in Releases.",
      'Repeating events: turn on Repeat in the event card — every N days, weeks, months or years, until a date or N times.',
      'Upcoming events of a series show dashed until you confirm them (menu or card); confirmed ones keep their place when the series changes. «Delete series» keeps the confirmed ones.',
      'Highlight is shared: what you highlight, everyone sees, with your name on the chip.',
      'Backlog: «+ New task» is the first card of the list.',
      'View menu in the toolbar: Full titles, Badges, Release marks, Person colours, Only me, and the hidden rows and lanes, all in one place.',
    ],
    changed: [
      "A pinned deploy date stays put when you move or stretch the task. The deploy week can't be before the last week of the work: the card won't save it.",
      'When a move would push the end past the deploy date, a dialog asks what to do — pin to the new end, deploy at the end, not deploying, or cancel (for a group, one answer for all).',
      'While dragging a task with a deploy date, a flag marks the deploy week and the outline turns red past it.',
      'Dropping a release on a week before its work ends offers to move the work so it ends that week.',
      '15 minutes without mouse or keyboard: you leave the «who\'s here» row and your selection clears; the first movement brings you back.',
      'Range Reset makes the range follow today again; a range you set yourself is remembered until you reset it.',
      'Deleting a Timeline lane: its projects leave the Timeline until you add them to another lane (the dialog says so).',
    ],
    fixed: [
      '«… changed … after you» showed again on every screen switch; now once.',
      'A drag interrupted by the system (touch, focus loss) no longer leaves the task «in hand» for others.',
    ],
  },
  {
    date: '2026-09-10',
    version: 3,
    name: 'Eyes on the Table',
    added: [
      'Highlight a task, event or vacation on the whole timeline (right-click → Highlight): a band over its dates, everything outside them fades. Esc removes it.',
      'Jump to the task from a pinned pill in the header: the grid scrolls to the bar.',
      'Agenda shows resolved questions too (Open | Resolved). Projects in the agenda collapse.',
      'See where others are: tasks someone selected, drags or has open get a frame in their colour. People who left stay as a faded avatar with «last seen».',
    ],
    changed: [
      'Task card and Help: buttons and tabs stay put, only the content scrolls; the card uses the full screen height. A new task has no Questions tab until saved.',
      'Full titles: only rows whose titles do not fit grow.',
      'Hidden projects, people and header lanes live in a «N hidden» menu in the toolbar, with «Show all».',
      'The Badges toggle hides both the «?» counter and the design dot.',
    ],
    fixed: [
      'Delete, arrows and Ctrl+D work on a group selected with a frame.',
      'Esc in an open menu only closes the menu, the selection stays.',
    ],
  },
  {
    date: '2026-09-08',
    version: 2,
    name: 'Multi-Tabling',
    added: [
      'Select several tasks: Ctrl+click or drag a frame over empty grid. Move them together, duplicate, send to the backlog or delete in one go.',
      'Questions on a task: a Questions tab in the card, a «?» marker on the bar, and an Agenda panel where they get answered and resolved.',
      "Pin a task to the header (Ctrl+P or the menu): it shows in its project's lane above the grid, for everyone.",
      'Right-click menus on tasks, rows, header pills and empty grid. «New task here» shows where the task will land.',
      'Projects: create with subprojects, edit, delete, reorder by the handle, hide for yourself.',
      'Teams in the task card fill in the assignees.',
      'Full titles toggle: taller bars, titles wrap instead of being cut.',
      'People: colour bars by person, task count next to the name.',
      'Delete key deletes the task under focus.',
    ],
    changed: [
      'Default range starts at the first full week of last month and covers six months of full weeks. Picked dates snap to whole weeks.',
      'Header: who is here as a stack of avatars with your connection dot, tools in the middle, email next to Sign out.',
      'Rows without visible tasks collapse to a title line; row actions (add, edit, hide) appear on hover in one place.',
      'One height for every toolbar control.',
      'Help window (the «?») is wider and keeps one size for Shortcuts and What\'s new.',
      'Deleting a project asks for confirmation inside the card, like deleting a task.',
    ],
    fixed: [
      "A new task's card no longer lists everyone present as co-editors.",
      'Bars had lost their gap and the header band had a stray gap below it.',
      'Long titles no longer break mid-word; the smaller font is used only when the title does not fit.',
      'Mouse wheel over row labels scrolls the grid; drags auto-scroll near the edges.',
    ],
  },
  {
    date: '2026-09-07',
    version: 1,
    name: 'Seat Open',
    added: [
      'The new roadmap: sign in with your Google account, no shared password.',
      'Everyone works in the same roadmap live. You see who is here and what they drag; the card shows who else is editing and merges their changes.',
      'Projects view: drag to move, drag an edge to resize, Shift for day precision, arrow keys, Ctrl+D to clone.',
      'People view: a row per person, drag a task onto another row to reassign, vacations as shadows over the bars, reorder people.',
      'Backlog and Design lists in a side panel; drag a card onto the grid to plan it.',
      'Events and vacations above the grid, edited by double-click.',
      'Date range with Quarter, Half year and Year presets. Shortcuts help behind «?».',
    ],
    changed: [
      'Tasks keep exact dates instead of week numbers; months and quarters follow the calendar.',
    ],
  },
];
